import { POLYGON_CHAIN_ID, RELAYER_URL, REMOTE_SIGNING_URL } from '@/constants/polymarket'
import { PUSD_CONTRACT_ADDRESS, USDC_E_CONTRACT_ADDRESS } from '@/constants/tokens'
import {
	deriveDepositWalletAddress,
	ensureDepositWalletReady,
} from '@/lib/wallets/polymarket-deposit'
import { checkAllApprovals, createAllApprovalTxs } from '@/utils/approvals'
import { deriveSafeAddress } from '@/utils/safe'
import { captureApiError } from '@/utils/sentry-helpers'
import { buildWrapTx } from '@/utils/wrap-collateral'
import { RelayClient, RelayerTransactionState } from '@polymarket/builder-relayer-client'
import { BuilderConfig } from '@polymarket/builder-signing-sdk'
import type { Address, PublicClient, WalletClient } from 'viem'

const erc20BalanceAbi = [
	{
		name: 'balanceOf',
		type: 'function',
		stateMutability: 'view',
		inputs: [{ name: 'account', type: 'address' }],
		outputs: [{ name: '', type: 'uint256' }],
	},
] as const

/**
 * Polymarket V1 wallet identity is a Safe deterministically derived from the
 * EOA. V2 keeps that derivation on-chain until the custom-wallet migration
 * lands; this helper names that "legacy" so consumer code reads as
 * Polymarket-funder-address rather than Safe-address.
 */
export function resolveLegacyPolymarketFunderAddress(
	ownerAddress: string | undefined
): `0x${string}` | undefined {
	return ownerAddress ? (deriveSafeAddress(ownerAddress) as `0x${string}`) : undefined
}

/**
 * Read the pUSD balance held on a Safe address. Used by the dispatch logic
 * below to distinguish a real legacy Safe user (Safe with funds → Safe path)
 * from a post-migration orphan (deployed Safe but already drained → fall
 * through to deposit-wallet path).
 *
 * Returns 0n on RPC failure — fail-closed for the dispatch (i.e. treat as
 * empty Safe) so a transient RPC outage doesn't lock users into Safe path.
 */
async function readSafePusdBalance(
	publicClient: PublicClient | null,
	safeAddress: `0x${string}`
): Promise<bigint> {
	if (!publicClient) return 0n
	try {
		const balance = (await publicClient.readContract({
			address: PUSD_CONTRACT_ADDRESS as `0x${string}`,
			abi: erc20BalanceAbi,
			functionName: 'balanceOf',
			args: [safeAddress],
		})) as bigint
		return balance
	} catch (err) {
		console.warn('[wallet-readiness] readSafePusdBalance failed', {
			safeAddress,
			error: err instanceof Error ? err.message : String(err),
		})
		return 0n
	}
}

export function createPolymarketRelayClient({
	relayerUrl = RELAYER_URL,
	walletClient,
}: {
	relayerUrl?: string
	walletClient: WalletClient
}): RelayClient {
	const relayBuilderConfig = new BuilderConfig({
		remoteBuilderConfig: { url: REMOTE_SIGNING_URL() },
	})

	return new RelayClient(
		relayerUrl,
		POLYGON_CHAIN_ID,
		// biome-ignore lint/suspicious/noExplicitAny: RelayClient expects a wallet client shape that doesn't match viem's exported type
		walletClient as any,
		relayBuilderConfig
	)
}

/**
 * Asks the relay if the funder is already deployed; falls back to checking
 * code at the address if the relay's getDeployed isn't reachable.
 */
export async function isPolymarketFunderDeployed({
	relayClient,
	publicClient,
	funderAddress,
}: {
	relayClient: RelayClient
	publicClient: PublicClient | null
	funderAddress: `0x${string}`
}): Promise<boolean> {
	try {
		// biome-ignore lint/suspicious/noExplicitAny: getDeployed not in RelayClient type definition
		const deployedViaRelayer = await (relayClient as any).getDeployed(funderAddress)
		if (deployedViaRelayer) return true
	} catch {
		// Fall through to bytecode check.
	}

	const code = await publicClient?.getCode({ address: funderAddress })
	return !!code && code !== '0x'
}

/** Deploy + wait for STATE_MINED / CONFIRMED / FAILED. */
export async function deployPolymarketFunder(relayClient: RelayClient): Promise<void> {
	const response = await relayClient.deploy()
	await relayClient.pollUntilState(
		response.transactionID,
		[
			RelayerTransactionState.STATE_MINED,
			RelayerTransactionState.STATE_CONFIRMED,
			RelayerTransactionState.STATE_FAILED,
		],
		'60',
		3000
	)
}

export async function ensurePolymarketApprovals({
	relayClient,
	funderAddress,
}: {
	relayClient: RelayClient
	funderAddress: string
}): Promise<void> {
	const approvalStatus = await checkAllApprovals(funderAddress)
	if (approvalStatus.allApproved) return

	const response = await relayClient.execute(
		createAllApprovalTxs(),
		'Set token approvals for trading'
	)
	await response.wait()
}

/**
 * V2 collateral migration: if the funder holds USDC.e, wrap it to pUSD.
 * Idempotent — a non-zero pUSD balance is the "already migrated" signal,
 * controllable via onlyWhenPusdEmpty.
 *
 * onlyWhenPusdEmpty: true  → only wrap when pUSD is zero (initialization-time
 *                            "did the user upgrade?" check, run once on init).
 * onlyWhenPusdEmpty: false → wrap any stranded USDC.e even if pUSD is non-zero
 *                            (background poller catching late deposits).
 */
export async function wrapStrandedUsdceIfNeeded({
	relayClient,
	publicClient,
	funderAddress,
	onlyWhenPusdEmpty = false,
}: {
	relayClient: RelayClient
	publicClient: PublicClient | null
	funderAddress: `0x${string}`
	onlyWhenPusdEmpty?: boolean
}): Promise<void> {
	if (!publicClient) return

	const [usdceBalance, pusdBalance] = await Promise.all([
		publicClient.readContract({
			address: USDC_E_CONTRACT_ADDRESS as `0x${string}`,
			abi: erc20BalanceAbi,
			functionName: 'balanceOf',
			args: [funderAddress],
		}),
		publicClient.readContract({
			address: PUSD_CONTRACT_ADDRESS as `0x${string}`,
			abi: erc20BalanceAbi,
			functionName: 'balanceOf',
			args: [funderAddress],
		}),
	])

	if (usdceBalance === 0n) return
	if (onlyWhenPusdEmpty && pusdBalance > 0n) return

	const wrapResponse = await relayClient.execute(
		[buildWrapTx(funderAddress, usdceBalance)],
		'Wrap USDC.e to pUSD'
	)
	await wrapResponse.wait()
}

/**
 * Thrown when the user explicitly selected the Safe path (wallet switcher)
 * but the Safe has no bytecode on-chain. Deploying a fresh Safe now would
 * mint a post-V2-cutover Safe that Polymarket rejects (see
 * LEGACY_BROKEN_SAFES) — so a preference-initiated Safe path must never
 * auto-deploy. Callers catch this to auto-revert the preference to
 * 'deposit'.
 */
export class PolymarketSafeUnavailableError extends Error {
	constructor(safeAddress: string) {
		super(`Legacy Safe ${safeAddress} is not deployed — Safe path unavailable`)
		this.name = 'PolymarketSafeUnavailableError'
	}
}

/**
 * Ensures the user's Polymarket wallet is ready to trade.
 *
 * Dispatches on user kind:
 *   - polymarketSafe NOT NULL → existing Safe user; run deploy/approve/wrap via RelayClient.
 *   - polymarketSafe NULL     → new deposit-wallet user; run ensureDepositWalletReady.
 *
 * Returns the funder address (Safe or deposit wallet) that should be passed to
 * createAuthenticatedPolymarketClobClient / createDepositWalletClobClient.
 */
export async function ensurePolymarketWalletReady({
	polymarketSafe,
	eoaAddress,
	walletClient,
	publicClient,
	relayerUrl = RELAYER_URL,
	onProgress,
	forceDepositWallet = false,
	activeWallet = null,
}: {
	polymarketSafe: string | null
	eoaAddress: string
	walletClient: WalletClient
	publicClient: PublicClient | null
	relayerUrl?: string
	onProgress?: (message: string) => void
	// Skip the Safe path entirely even if the user has polymarketSafe set OR
	// a deployed legacy Safe with a balance. Used for the LEGACY_BROKEN_SAFES
	// allow-list — those Safes are deployed and funded but rejected by
	// Polymarket V2; the upgrade modal drains them into the deposit wallet.
	forceDepositWallet?: boolean
	// Wallet-switcher preference for legacy Safe users. 'deposit' behaves like
	// forceDepositWallet; 'safe' additionally forbids auto-deploying an
	// undeployed Safe (post-cutover deploys are rejected by Polymarket).
	activeWallet?: 'safe' | 'deposit' | null
}): Promise<{
	funderAddress: `0x${string}`
	relayClient: RelayClient | null
	walletKind: 'safe' | 'deposit'
}> {
	// Resolve the candidate Safe address. We then decide whether to actually
	// take the Safe path based on two signals:
	//   1) DB: polymarket_safe explicitly assigned (legacy user, authoritative)
	//   2) On-chain: derived Safe is deployed AND still holds pUSD (rescue
	//      path for users whose POST /api/user/profile silently failed —
	//      runtime polymarketSafe ends up null even though DB has the Safe).
	//
	// An empty deployed Safe (post-migration: sweep ran, Safe drained, DB
	// flipped to NULL) deliberately falls through to the deposit-wallet path
	// instead of being preferred — without the balance gate the auto-detect
	// would re-claim migrated users for the Safe path forever.
	const legacySafeAddress = resolveLegacyPolymarketFunderAddress(eoaAddress)
	const safeFunderAddress = (polymarketSafe || legacySafeAddress) as `0x${string}` | undefined
	const skipSafePath = forceDepositWallet || activeWallet === 'deposit'

	if (safeFunderAddress && !skipSafePath) {
		const funderAddress = safeFunderAddress
		const relay = createPolymarketRelayClient({ relayerUrl, walletClient })

		onProgress?.('Checking wallet...')
		const [deployed, safeBalance] = await Promise.all([
			isPolymarketFunderDeployed({
				relayClient: relay,
				publicClient,
				funderAddress,
			}),
			readSafePusdBalance(publicClient, funderAddress),
		])

		// EMPTY_THRESHOLD = 1000 raw = 0.001 pUSD. Rounding dust from a prior
		// sweep doesn't hold us in the Safe path.
		const EMPTY_THRESHOLD = 1000n
		const explicitSafe = !!polymarketSafe
		const orphanSafeWithFunds = !explicitSafe && deployed && safeBalance > EMPTY_THRESHOLD

		if (explicitSafe || orphanSafeWithFunds) {
			if (!deployed && explicitSafe) {
				// Preference-initiated Safe path must NEVER deploy: any Safe
				// deployed after the V2 cutover (2026-05-04) is rejected by
				// Polymarket — deploying here would mint a new broken Safe.
				// Legacy default (activeWallet null) keeps prior behavior for
				// pre-cutover users whose deploy was pending.
				if (activeWallet === 'safe') {
					throw new PolymarketSafeUnavailableError(funderAddress)
				}
				onProgress?.('Deploying wallet...')
				await deployPolymarketFunder(relay)
			}

			onProgress?.('Setting approvals...')
			await ensurePolymarketApprovals({ relayClient: relay, funderAddress })

			onProgress?.('Wrapping USDC.e to pUSD...')
			await wrapStrandedUsdceIfNeeded({
				relayClient: relay,
				publicClient,
				funderAddress,
				onlyWhenPusdEmpty: true,
			})

			return { funderAddress, relayClient: relay, walletKind: 'safe' }
		}
	}

	// --- Deposit-wallet path (new users) ---
	const depositWalletAddress = deriveDepositWalletAddress(eoaAddress as Address)

	onProgress?.('Preparing deposit wallet...')
	try {
		await ensureDepositWalletReady({
			walletClient,
			publicClient: publicClient as PublicClient,
			depositWalletAddress,
			relayerUrl,
		})
	} catch (err) {
		captureApiError(err, {
			endpoint: 'polymarket_deposit_ready',
			tags: { category: 'polymarket-deposit', flow: 'ready' },
			extra: { depositWallet: depositWalletAddress, userId: undefined },
		})
		throw err
	}

	return {
		funderAddress: depositWalletAddress as `0x${string}`,
		relayClient: null,
		walletKind: 'deposit',
	}
}
